import { signInWithPopup } from 'firebase/auth';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AnimationBox from '../../components/common/AnimationBox';
import Header from '../../components/layout/Header';
import { auth, facebookProvider, googleProvider, microsoftProvider } from '../../config/firebase';
import { useAuthStore } from '../../stores/authStore';
import '../../styles/LoginPage.css';
import { generateDeviceIdWithTimestamp } from '../../utils/deviceHelper.js';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((state) => state.login);

  // Read returnTo from navigation state (set by CourseDetailsPage when unauthenticated user clicks Enroll)
  const returnTo = location.state?.returnTo || null;

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state?.email) {
        setEmail(location.state.email);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setPasswordError('');
    setSuccessMessage('');

    let valid = true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setEmailError('Email is required.');
      valid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address.');
      valid = false;
    }

    if (!password) {
      setPasswordError('Password is required.');
      valid = false;
    }

    if (!valid) return;

    try {
      const loginResult = await login(email, password, null);

      console.log('🔍 Email/Password login result received in LoginPage:', loginResult);

      if (loginResult && loginResult.success) {
        console.log('🔍 Checking profile status:', loginResult.is_profile_set, typeof loginResult.is_profile_set);
        if (loginResult.is_profile_set) {
          console.log('✅ Profile is set, navigating to dashboard');
          // Old navigation (commented out — replaced by returnTo-aware navigation below)
          // navigate('/dashboard');
          // If we came from a course page (Enroll Now while logged out), go back there
          navigate(returnTo || '/dashboard');
        } else {
          console.log('⚠️ Profile not set, navigating to user details');
          navigate('/user-details');
        }
      } else if (loginResult?.error) {
        setPasswordError(loginResult.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      setPasswordError(error.response?.data?.message || error.response?.data?.error || error.message);
    }
  };

  const handleSocialSignIn = async (provider, providerName) => {
    try {
      const result = await signInWithPopup(auth, provider);
      console.log(`🔍 ${providerName} sign-in result:`, result.user);

      const deviceId = generateDeviceIdWithTimestamp();
      console.log(`🔐 Generated deviceId for ${providerName} login:`, deviceId);

      const loginResult = await useAuthStore.getState().thirdPartyLogin(result.user, deviceId);
      console.log(`🔍 ${providerName} login result received in LoginPage:`, loginResult);

      if (loginResult?.success) {
        console.log('🔍 Checking profile status:', loginResult.is_profile_set, typeof loginResult.is_profile_set);
        if (loginResult.is_profile_set) {
          console.log(`✅ Profile is set, navigating to /dashboard`);
          // Old navigation (commented out — replaced by returnTo-aware navigation below)
          // navigate('/dashboard');
          // If we came from a course page (Enroll Now while logged out), go back there
          navigate(returnTo || '/dashboard');
        } else {
          console.log(`✅ Profile NOT set, navigating to /user-details`);
          navigate('/user-details');
        }
      } else if (loginResult?.error) {
        console.error(`${providerName} sign-in failed:`, loginResult.error);
        setPasswordError(loginResult.error);
      }
    } catch (error) {
      console.error(`Error signing in with ${providerName}:`, error);
      setPasswordError(error.response?.data?.message || error.response?.data?.error || error.message);
    }
  };

  const handleGoogleSignIn = () => handleSocialSignIn(googleProvider, 'Google');
  const handleMicrosoftSignIn = () => handleSocialSignIn(microsoftProvider, 'Microsoft');
  const handleFacebookSignIn = () => handleSocialSignIn(facebookProvider, 'Facebook');

  return (
    <div className="login-container">
      <Header />
      <AnimationBox className="login-box">
        <h4 className='h4'>Sign In</h4>
        {successMessage && (
          <div className="success-message" style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '15px',
            border: '1px solid #c3e6cb'
          }}>
            {successMessage}
          </div>
        )}
        <div className='social-login'>
          <button className='social-btn google body2' onClick={handleGoogleSignIn}>Continue with Google<img src='google.svg' alt='google' /></button>
          <button className='social-btn apple body2'>Continue with Apple<img src='apple.svg' alt='apple' /></button>
          <button className='social-btn microsoft body2' onClick={handleMicrosoftSignIn}>Continue with Microsoft<img src='microsoft.svg' alt='microsoft' /></button>
          <button className='social-btn facebook body2' onClick={handleFacebookSignIn}>Continue with Facebook<img src='facebook.svg' alt='facebook' /></button>
        </div>
        <div className="divider"><span>OR</span></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address..."
            />
            {emailError && <div className="error-message">{emailError}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password..."
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeIcon size={20} /> : <EyeOffIcon size={20} />}
              </button>
            </div>
            {passwordError && <div className="error-message">{passwordError}</div>}
          </div>
          <button type="submit" className="body3-bold login-button">Sign in</button>
        </form>
        <div className='body4 forgot-password'><a href="/forgot-password">Forgot password?</a></div>
        <hr className="hr" />
        <div className="signup">
          <span className='body2'>New to the platform?</span>
          <button
            className='body3-bold'
            onClick={() => navigate('/signup')}
          >
            Sign up
          </button>
        </div>
      </AnimationBox>
    </div>
  );
};

export default LoginPage;